import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { taskService } from '../services/taskService';
import { TaskDraftDTO, TaskDraftStatus } from '../types/TaskDraftDTO';
import { Button, Typography, Box, Paper, CircularProgress, Alert, Grid, Chip, Avatar, Stack, TextField, FormControl, InputLabel, Select, MenuItem, OutlinedInput } from '@mui/material';
import { Edit, Save, Cancel, Link as LinkIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { ManagerRole, UserType } from '../types/auth';
import { Media } from '../types/media';
import { format } from 'date-fns';
import { Category } from '../types/category';
import { categoryService } from '../services/categoryService';
import { TaskDraftCategory } from '../types/TaskDraftCategory';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Skill } from '../types/skill';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const TaskDraftDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { state } = useAuth();
    const authenticatedUser = state.user;

    const [taskDraft, setTaskDraft] = useState<TaskDraftDTO | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);

    const [editTitle, setEditTitle] = useState<string>('');
    const [editDescription, setEditDescription] = useState<string>('');
    const [editPrice, setEditPrice] = useState<string>('');
    const [editDeadline, setEditDeadline] = useState<Date | null>(null);
    const [editRequirements, setEditRequirements] = useState<string>('');
    const [editCategories, setEditCategories] = useState<Category[]>([]);

    const [availableCategories, setAvailableCategories] = useState<Category[]>([]);

    const [mediaFiles, setMediaFiles] = useState<Media[]>([]);

    const isOrganizationManager = authenticatedUser?.type === UserType.MANAGER && authenticatedUser.manager?.roles?.includes(ManagerRole.ORGANIZATION_MANAGER);

    useEffect(() => {
        const fetchTaskDraftAndData = async () => {
            if (!id) {
                setError('ID черновика задачи отсутствует.');
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                const taskDraftData = await taskService.getTaskDraftById(id);
                setTaskDraft(taskDraftData);
                setEditTitle(taskDraftData.title);
                setEditDescription(taskDraftData.description);
                setEditPrice(taskDraftData.priceInKopecks ? (taskDraftData.priceInKopecks / 100).toString() : '');
                setEditDeadline(taskDraftData.deadline ? new Date(taskDraftData.deadline) : null);
                setEditRequirements(taskDraftData.requirements || '');
                setEditCategories(taskDraftData.taskDraftCategories?.map(tdc => tdc.category) || []);
                setMediaFiles(taskDraftData.media || []);

                const categoriesResponse = await categoryService.getAllCategories();
                setAvailableCategories(categoriesResponse.data);

            } catch (err) {
                setError('Не удалось загрузить детали черновика задачи или данные.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchTaskDraftAndData();
    }, [id]);

    const handleSave = async () => {
        if (!taskDraft || !id) return;
        try {
            const updatedTaskDraft: TaskDraftDTO = {
                ...taskDraft,
                title: editTitle,
                description: editDescription,
                priceInKopecks: editPrice ? parseFloat(editPrice) * 100 : undefined,
                deadline: editDeadline ? editDeadline.toISOString() : undefined,
                requirements: editRequirements,
                taskDraftCategories: editCategories.map(c => ({ categoryId: c.id!, taskDraftId: id!, category: c } as TaskDraftCategory)),
                updatedAt: new Date().toISOString(),
            };
            await taskService.updateTaskDraft(id, updatedTaskDraft);
            setTaskDraft(updatedTaskDraft);
            setIsEditing(false);
        } catch (err) {
            setError('Не удалось сохранить черновик задачи.');
            console.error(err);
        }
    };

    const handleCancelEdit = () => {
        if (taskDraft) {
            setEditTitle(taskDraft.title);
            setEditDescription(taskDraft.description);
            setEditPrice(taskDraft.priceInKopecks ? (taskDraft.priceInKopecks / 100).toString() : '');
            setEditDeadline(taskDraft.deadline ? new Date(taskDraft.deadline) : null);
            setEditRequirements(taskDraft.requirements || '');
            setEditCategories(taskDraft.taskDraftCategories?.map(tdc => tdc.category) || []);
        }
        setIsEditing(false);
    };

    const handleAskQuestion = () => {
        console.log('Ask question to curator');
    };

    const getStatusDisplayName = (status?: TaskDraftStatus) => {
        switch (status) {
            case TaskDraftStatus.IN_PROCESS: return 'В процессе';
            case TaskDraftStatus.REJECTED: return 'Отклонено';
            case TaskDraftStatus.FOR_REVISION: return 'На доработку';
            case TaskDraftStatus.READY: return 'Готово';
            case TaskDraftStatus.PAID: return 'Оплачено';
            default: return status || 'Неизвестно';
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    }

    if (error) {
        return <Box sx={{ mt: 4 }}><Alert severity="error">{error}</Alert></Box>;
    }

    if (!taskDraft) {
        return <Box sx={{ mt: 4 }}><Alert severity="info">Черновик задачи не найден.</Alert></Box>;
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1200, margin: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>Дата создания: {taskDraft.createdAt ? format(new Date(taskDraft.createdAt), 'dd.MM.yyyy') : '—'}</Typography>
                    {isEditing ? (
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Название задачи"
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                    ) : (
                        <Typography variant="h4" component="h1" gutterBottom>{taskDraft.title}</Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {isEditing ? (
                        <TextField
                            label="Бюджет в рублях"
                            type="number"
                            value={editPrice}
                            onChange={(e) => setEditPrice(e.target.value)}
                            sx={{ mr: 2, width: 150 }}
                        />
                    ) : (
                        <Typography variant="h5" component="span" sx={{ mr: 2 }}>{taskDraft.priceInKopecks ? `${taskDraft.priceInKopecks / 100}₽` : '—'}</Typography>
                    )}
                    {isOrganizationManager && !isEditing && (
                        <Button variant="contained" startIcon={<Edit />} onClick={() => setIsEditing(true)}>Редактировать задачу</Button>
                    )}
                    {isOrganizationManager && isEditing && (
                        <Box>
                            <Button variant="outlined" startIcon={<Cancel />} onClick={handleCancelEdit} sx={{ mr: 1 }}>Отмена</Button>
                            <Button variant="contained" startIcon={<Save />} onClick={handleSave}>Сохранить Изменения</Button>
                        </Box>
                    )}
                </Box>
            </Box>

            <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
                <Grid container spacing={2}>
                    <Grid item xs={2}>
                        <Typography variant="body2" color="textSecondary"></Typography>
                        {isEditing ? (
                            <FormControl fullWidth size="small">
                                <InputLabel id="edit-categories-label">Категория</InputLabel>
                                <Select
                                    labelId="edit-categories-label"
                                    multiple
                                    value={editCategories.map(c => c.id)}
                                    onChange={(event) => {
                                        const value = event.target.value as string[];
                                        setEditCategories(availableCategories.filter(c => value.includes(c.id!)));
                                    }}
                                    input={<OutlinedInput id="select-edit-categories-chip" label="Категория" />}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => {
                                                const category = availableCategories.find(c => c.id === value);
                                                return <Chip key={value} label={category?.title || value} />;
                                            })}
                                        </Box>
                                    )}
                                    MenuProps={MenuProps}
                                >
                                    {availableCategories.map((category) => (
                                        <MenuItem key={category.id} value={category.id}>
                                            {category.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                {taskDraft.taskDraftCategories && taskDraft.taskDraftCategories.length > 0 ? (
                                    taskDraft.taskDraftCategories.map(tdc => <Chip key={tdc.category.id} label={tdc.category.title} />)
                                ) : (
                                    <Typography variant="subtitle1">—</Typography>
                                )}
                            </Stack>
                        )}
                    </Grid>
                    <Grid item xs={2}>
                        <Typography variant="body2" color="textSecondary">Статус</Typography>
                        <Typography variant="subtitle1">{getStatusDisplayName(taskDraft.status)}</Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography variant="body2" color="textSecondary">Принятие</Typography>
                        <Typography variant="subtitle1">{taskDraft.createdAt ? format(new Date(taskDraft.createdAt), 'dd.MM.yyyy') : '—'} {/* Placeholder */}</Typography>
                    </Grid>
                    <Grid item xs={2}>
                        <Typography variant="body2" color="textSecondary"></Typography>
                        {isEditing ? (
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Дедлайн"
                                    value={editDeadline}
                                    onChange={(newValue) => setEditDeadline(newValue)}
                                    slots={{ textField: (params) => <TextField {...params} fullWidth size="small" /> }}
                                />
                            </LocalizationProvider>
                        ) : (
                            <Typography variant="subtitle1">{taskDraft.deadline ? format(new Date(taskDraft.deadline), 'dd.MM.yyyy') : '—'}</Typography>
                        )}
                    </Grid>
                    <Grid item xs={4}>
                        <Typography variant="body2" color="textSecondary">Заказчик</Typography>
                        <Typography variant="subtitle1">{taskDraft.organization?.name || '—'}</Typography>
                    </Grid>
                </Grid>
            </Paper>

            {/* Task Description Section */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Описание задачи:</Typography>
                {isEditing ? (
                    <TextField
                        fullWidth
                        multiline
                        rows={6}
                        variant="outlined"
                        label="Описание задачи"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                    />
                ) : (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{taskDraft.description}</Typography>
                )}
            </Box>

            {/* Skills Section */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Скиллы для задачи:</Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    {taskDraft.tasks && taskDraft.tasks.length > 0 ? (
                        (() => {
                            const uniqueSkills = new Map<string, Skill>();
                            taskDraft.tasks.forEach(task => {
                                task.taskSkills?.forEach(ts => {
                                    if (ts.skill && !uniqueSkills.has(ts.skill.id!)) {
                                        uniqueSkills.set(ts.skill.id!, ts.skill);
                                    }
                                });
                                task.taskRequiredSkills?.forEach(trs => {
                                    if (trs.skill && !uniqueSkills.has(trs.skill.id!)) {
                                        uniqueSkills.set(trs.skill.id!, trs.skill);
                                    }
                                });
                            });
                            const skillsToDisplay = Array.from(uniqueSkills.values());
                            return skillsToDisplay.length > 0 ? (
                                skillsToDisplay.map(skill => <Chip key={skill.id} label={skill.title} />)
                            ) : (
                                <Typography variant="body2" color="textSecondary">Навыки не указаны.</Typography>
                            );
                        })()
                    ) : (
                        <Typography variant="body2" color="textSecondary">Навыки не указаны.</Typography>
                    )}
                </Stack>
            </Box>

            {/* Uploaded Files Section */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Загруженные файлы:</Typography>
                <Stack spacing={1}>
                    {mediaFiles.length > 0 ? (
                        mediaFiles.map((mediaItem) => (
                            <Paper key={mediaItem.id} variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                {mediaItem.fileUrl.match(/\.(jpeg|jpg|gif|png|webp)$/i) ? (
                                    <img src={mediaItem.fileUrl} alt={mediaItem.name} style={{ width: 24, height: 24, objectFit: 'cover' }} />
                                ) : (
                                    <LinkIcon fontSize="small" />
                                )}
                                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                    <a href={mediaItem.fileUrl} target="_blank" rel="noopener noreferrer">{mediaItem.name}</a>
                                </Typography>
                                {isEditing && (
                                    <Button size="small" color="error" onClick={() => console.log(`Delete ${mediaItem.id}`)}>
                                        Удалить
                                    </Button>
                                )}
                            </Paper>
                        ))
                    ) : (
                        <Typography variant="body2" color="textSecondary">Файлы не загружены.</Typography>
                    )}
                </Stack>
            </Box>

            {/* Curator Section */}
            <Paper elevation={1} sx={{ p: 3, mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar src={taskDraft.manager?.avatar} sx={{ mr: 2 }} />
                    <Box>
                        <Typography variant="body2" color="textSecondary">Куратор</Typography>
                        <Typography variant="subtitle1">{taskDraft.manager?.fullName || 'Не указан'}</Typography>
                    </Box>
                </Box>
                <Button variant="contained" onClick={handleAskQuestion}>ЗАДАТЬ ВОПРОС ПО ЗАДАЧЕ</Button>
            </Paper>

            {/* Executors Section (Placeholder for now) */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Исполнитель</Typography>
                <Stack direction="row" spacing={2} sx={{ overflowX: 'auto', pb: 1 }}>
                    {taskDraft.tasks && taskDraft.tasks.length > 0 ? (
                        taskDraft.tasks.map(task => (
                            task.taskUsers && task.taskUsers.length > 0 ? (
                                task.taskUsers.map(taskUser => (
                                    <Paper key={taskUser.id} elevation={1} sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 150 }}>
                                        <Avatar src={taskUser.user?.avatar} sx={{ width: 56, height: 56, mb: 1 }}>
                                            {taskUser.user?.fullName ? taskUser.user.fullName.split(' ').map(n => n[0]).join('') : (taskUser.user?.personalData?.fullName ? taskUser.user.personalData.fullName.split(' ').map(n => n[0]).join('') : '—')}
                                        </Avatar>
                                        <Typography variant="subtitle2">{taskUser.user?.fullName || taskUser.user?.personalData?.fullName || '—'}</Typography>
                                        {/* Здесь можно добавить дополнительную информацию об исполнителе, если она есть в TaskUserDTO */}
                                        {/* <Typography variant="body2" color="textSecondary">{taskUser.status}</Typography> */}
                                    </Paper>
                                ))
                            ) : (
                                <Typography variant="body2" color="textSecondary">Исполнители для этой задачи не назначены.</Typography>
                            )
                        ))
                    ) : (
                        <Typography variant="body2" color="textSecondary">Задачи не декомпозированы.</Typography>
                    )}
                </Stack>
            </Box>

            {isEditing && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                    <Button variant="outlined" startIcon={<Cancel />} onClick={handleCancelEdit}>Отмена</Button>
                    <Button variant="contained" startIcon={<Save />} onClick={handleSave}>Сохранить Изменения</Button>
                </Box>
            )}
        </Box>
    );
};

export default TaskDraftDetailsPage;
